import { CorpoCube } from "./corpo.model"
import { FormatType } from "./enumType"

const formattedCells = new Map<string, string>();
let formattingQueue: Promise<void> = Promise.resolve();

export async function addFormatting(cellAddress: string, data: CorpoCube & { format: FormatType } ) {
    formattingQueue = formattingQueue.then(() =>_applyFormatting(cellAddress, data));
    return formattingQueue;
}

async function _applyFormatting(cellAddress: string, data: CorpoCube & { format: FormatType } ) {
    try {
        await Excel.run(async context => {
            const appliedKey = `${data.link}|${data.numericValue}|${data.format}|${data.unit}`;
            const cacheKey = cellAddress;
            if (formattedCells.get(cacheKey) === appliedKey) return;


            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const ref = cellAddress?.includes("!") ? cellAddress.split("!")[1] : cellAddress;
            const range = sheet.getRange(ref);

            range.load(["hyperlink", "formulas", "format/columnWidth"])
            await context.sync();

            if (range.hyperlink?.address != data.link) {
                const originalFormula = range.formulas;
                range.hyperlink = {
                    address: data.link,
                    textToDisplay: String(data.numericValue),
                    screenTip: "Go to source"
                }

                range.format.font.color = "#0563C1"
                range.format.font.underline = Excel.RangeUnderlineStyle.single

                range.formulas = originalFormula
            }

            const numberFormat = getNumberFormat(data.format, data.unit);
            if (numberFormat) {
                range.numberFormat = [[numberFormat]];
            }

            if (range.format.columnWidth < 110) {
                range.format.columnWidth = 110;
            }

            await context.sync();

            formattedCells.set(cacheKey, appliedKey);
        });
    } catch (err) {
        console.error("Formatting error:", cellAddress, err);
    }
}

export async function clearFormatting(cellAddress: string) {
    if (!cellAddress) return;

    formattingQueue = formattingQueue.then(async () => {
        try {
            await Excel.run(async context => {
                let sheet: Excel.Worksheet;
                let ref: string;

                if (cellAddress.includes("!")) {
                    const [sheetName, cellRef] = cellAddress.split("!");
                    const cleanSheetName = sheetName.replace(/^'|'$/g, "");
                    sheet = context.workbook.worksheets.getItem(cleanSheetName);
                    ref = cellRef;
                } else {
                    sheet = context.workbook.worksheets.getActiveWorksheet();
                    ref = cellAddress;
                }

                const range = sheet.getRange(ref);
                range.clear(Excel.ClearApplyTo.removeHyperlinks);
                await context.sync();
                formattedCells.delete(cellAddress);
            });
        } catch (err) {
            console.error("Error clearFormatting:", err);
        }
    });
    return formattingQueue;
}


export async function clearAllFormattedCells() {
    const cellsToClear = Array.from(formattedCells.keys());
    if (cellsToClear.length === 0) return;

    formattingQueue = formattingQueue.then(async () => {
        try {
            await Excel.run(async context => {

                for (const cellAddress of cellsToClear) {

                    let sheet: Excel.Worksheet;
                    let ref: string;

                    if (cellAddress.includes("!")) {
                        const [sheetName, cellRef] = cellAddress.split("!");
                        const cleanSheetName = sheetName.replace(/^'|'$/g, "");
                        sheet = context.workbook.worksheets.getItem(cleanSheetName);
                        ref = cellRef;
                    } else {
                        sheet = context.workbook.worksheets.getActiveWorksheet();
                        ref = cellAddress;
                    }

                    const range = sheet.getRange(ref);
                    range.clear(Excel.ClearApplyTo.removeHyperlinks);
                }

                await context.sync();
                formattedCells.clear();
            });

        } catch (err) {
            console.error("Error clearAllFormattedCells:", err);
        }
    });

    return formattingQueue;
}

export function parseFormatType(value: string): FormatType {
    if (Object.keys(FormatType).includes(value)) {
        return FormatType[value as keyof typeof FormatType];
    }
    const indexOfValue = Object.values(FormatType).indexOf(value as FormatType);
    if (indexOfValue !== -1) {
        return FormatType[Object.keys(FormatType)[indexOfValue] as keyof typeof FormatType];
    }
    return FormatType.GENERAL;
}

export function getNumberFormat(format: FormatType, unit: string): string {
    if (!unit) return "";
    switch (format) {
        case FormatType.THOUSAND:
            return `#,##0,.0# "K ${unit}"; -#,##0,.0# "K ${unit}";0 "${unit}"`;
        case FormatType.MILLION:
            return `#,##0,,.0# "M ${unit}"; -#,##0,,.0# "M ${unit}";0 "${unit}"`;
        case FormatType.BILLION:
            return `#,##0,,,.0# "B ${unit}"; -#,##0,,,.0# "B ${unit}";0 "${unit}"`;
        default:
            return `General "${unit}";General "${unit}";0 "${unit}"`;
    }
}

